import { FC, useEffect, useId, useState } from 'react'
import { GENERATE_DEFAULT, GENERATE_POSTS_CHOICES, GENERATE_WEEKS_CHOICES } from 'utils/socialCampaign/schedule'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'

import { AutoAwesomeOutlined } from '@mui/icons-material'
import { CampaignPlanRequestDTO } from 'models/socialCampaign'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import Modal from 'components/common/Modals/Modal/Modal'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatCampaignDay } from 'utils/socialCampaign/campaignStatus'
import planStyles from './CampaignPlanAndContent.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  /** The first day the new posts go out (YYYY-MM-DD, campaign timezone) */
  startDay: string
  /** The generation is on its way: the dialog waits for it */
  pending: boolean
  onGenerate: (plan: CampaignPlanRequestDTO) => void
  onCancel: () => void
}

/**
 * "Generate new posts" (prototype v3 `renderGen`, v2 decision 18): the draft plan's sentence for the next wave,
 * "[4 posts] across [2 weeks]", from the day after the last post. The story goes on; the new posts arrive as drafts.
 * Opens on 4 posts across 2 weeks each time. A small accessible dialog without a close button (a sheet on a phone):
 * Escape, the scrim and Cancel close it, none of them while the generation is on its way.
 */
export const GeneratePostsDialog: FC<Props> = ({ open, startDay, pending, onGenerate, onCancel }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const ids = `campaign-gen-${useId().replace(/:/g, '')}`
  const [plan, setPlan] = useState(GENERATE_DEFAULT)

  useEffect(() => {
    if (open) setPlan(GENERATE_DEFAULT)
  }, [open])

  // the sentence reads as the selects' label (as in the plan of section 1): theirs stays for assistive technology
  const select = (id: string, label: string, value: number, choices: readonly number[], option: (count: number) => string, onChange: (count: number) => void) => (
    <>
      <label htmlFor={id} className={planStyles.srOnly}>{label}</label>
      <MUIDropdownInput id={id} isNativeSelect isChevronDecorative value={String(value)} disabled={pending} onChange={(event) => onChange(Number(event.target.value))}>
        {choices.map((count) => <option key={count} value={String(count)}>{option(count)}</option>)}
      </MUIDropdownInput>
    </>
  )

  return (
    <Modal
      isOpen={open}
      isAccessibleDialog
      ariaDescribedBy={`${ids}-body`}
      size="sm"
      isSheetOnPhone
      title={t('generate.title')}
      onClickOutside={pending ? undefined : onCancel}
      footerContent={
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" gap={1}>

          <MUIButton type="secondaryNoBorder" disabled={pending} onClick={onCancel}>{t('generate.cancel')}</MUIButton>

          {/* keeps the focus it had while the generation is on its way */}
          <MUIButton startIcon={<AutoAwesomeOutlined />} data-autofocus disabled={pending} isFocusableWhenDisabled onClick={() => onGenerate(plan)}>
            {t('generate.submit', { count: plan.posts })}
          </MUIButton>

        </Stack>
      }
    >
      <Stack gap="1.2rem">

        <div className={planStyles.planRow}>

          {select(`${ids}-posts`, t('generate.posts_label'), plan.posts, GENERATE_POSTS_CHOICES, (count) => t('plan.posts', { count }), (posts) => setPlan((current) => ({ ...current, posts })))}

          <Typography variant="text-sm" component="span" color={GRAY_900}>{t('plan.across')}</Typography>

          {select(`${ids}-weeks`, t('generate.weeks_label'), plan.weeks, GENERATE_WEEKS_CHOICES, (count) => t('plan.weeks', { count }), (weeks) => setPlan((current) => ({ ...current, weeks })))}

        </div>

        <Typography id={`${ids}-body`} variant="text-sm" component="p" color={GRAY_700}>
          {t('generate.body', { date: formatCampaignDay(startDay, i18n.language) })}
        </Typography>

      </Stack>
    </Modal>
  )
}
